/* import __COLOCATED_TEMPLATE__ from './summary-card.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import type { Job } from 'embercom/components/reporting/automation/ai-insights/optimize/jobs';
import {
  loadInsightsSummary,
  type InsightsSummaryResult,
} from 'embercom/components/reporting/automation/ai-insights/conversation-summary-data-resources';
import { trackedTask } from 'ember-resources/util/ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { restartableTask } from 'ember-concurrency-decorators';
import { inject as service } from '@ember/service';
import { type TaskGenerator } from 'ember-concurrency';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import type Range from 'embercom/models/reporting/range';
import { type FlexibleQueryTimeRange } from 'embercom/services/reporting-csv-export';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';
import { and } from 'embercom/components/reporting/automation/ai-insights/fin-insights/sankey-config';

interface Signature {
  Args: {
    selectedJob: Job;
    attributeId: string;
    attributeValue: string;
    dateRange: Range;
  };
}

export default class SummaryCard extends Component<Signature> {
  @service declare appService: any;
  @service declare reportingChartService: ReportingChartService;
  @service declare reportingMetrics: ReportingMetrics;
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;

  fetchSummaryTask = taskFor(this.loadSummary);

  summaryData = trackedTask(this, this.fetchSummaryTask, () => [
    this.processedFilters,
    this.args.dateRange,
    this.args.attributeId,
  ]);

  get attributeLabel(): string {
    let value = this.reportingUnderlyingDataService.mappedValueFor(
      this.args.attributeValue,
      this.args.attributeId,
    );

    if (typeof value === 'string') {
      return value;
    } else if (value && typeof value === 'object') {
      return value.toString();
    }

    return this.args.attributeValue || '';
  }

  get timeRange(): FlexibleQueryTimeRange {
    return {
      start: this.args.dateRange.startMoment.valueOf(),
      end: this.args.dateRange.endMoment.valueOf(),
      interval: this.args.dateRange.interval,
      property: this.metric.timeProperty,
    };
  }

  get metric() {
    return this.reportingMetrics.getMetricById('v1.new_conversations');
  }

  get processedFilters() {
    return {
      type: 'category',
      data: {
        property: this.args.attributeId,
        attribute: this.args.attributeId,
        values: [this.args.attributeValue],
      },
    };
  }

  get summaryText(): string {
    return this.fetchSummaryTask.lastSuccessful?.value?.summaryText || '';
  }

  get processedSummaryText(): string {
    let text = this.summaryText;
    if (!text) {
      return '';
    }

    // Replace markdown-style links [text](id) with clickable elements
    text = text.replace(/\[([^\]]+)\]\((\d+)\)/g, (_match, linkText, conversationId) => {
      return `<a href="#" class="text-blue-600 hover:text-blue-800 underline cursor-pointer" data-conversation-id="${conversationId}">${linkText}</a>`;
    });

    // Convert markdown headers (####) to HTML h4 tags
    text = text.replace(/^#### (.+)$/gm, '<h4 class="text-lg font-semibold mt-4 mb-2">$1</h4>');

    // Convert markdown bold (**text**) to HTML strong tags
    text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

    // Convert markdown bullet points to HTML list items
    text = text.replace(/^- (.+)$/gm, '<li class="ml-4">$1</li>');

    // Wrap consecutive list items in ul tags
    text = text.replace(/(<li[^>]*>.*<\/li>)(\s*<li[^>]*>.*<\/li>)*/gs, (match) => {
      return `<ul class="list-disc pl-4 mb-2">${match}</ul>`;
    });

    return text;
  }

  @restartableTask
  *loadSummary(): TaskGenerator<InsightsSummaryResult | null> {
    return yield loadInsightsSummary({
      appId: this.appService.app.id,
      adminId: this.appService.app.currentAdmin.id,
      filter: and(this.processedFilters),
      timeRange: this.timeRange,
      sortByAttributeId: 'conversation.first_user_conversation_part_created_at',
      sortDirection: 'desc',
      useSyntheticData: this.reportingChartService.useSyntheticData,
      timezone: this.appService.app.timezone,
      summaryType: 'attribute_drivers',
      selectedCxAttribute: this.args.attributeValue,
      maxConversationCount: 10000,
    });
  }

  // handleViewConversations = () => {
  //   // TODO: Implement view conversations functionality
  //   console.log('View conversations clicked for attribute:', this.args.attribute);
  // };
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::Optimize::SummaryCard': typeof SummaryCard;
    'reporting/automation/ai-insights/optimize/summary-card': typeof SummaryCard;
  }
}
